import { AppSessionBoundary } from '@/app/AppSessionBoundary';
import { registerAppDialogs } from '@/app/dialogRegistry';
import { queryClient } from '@/app/queryClient';
import { router } from '@/app/router';
import { registerSessionExpiredNavigator } from '@/app/sessionExpiration';
import { registerAppShortcuts } from '@/app/shortcutRegistry';
import { initAuth } from '@/features/authentication/stores/authStore';
import { PostHogAppProvider } from '@/shared/analytics/PostHogAppProvider';
import { initAnalytics } from '@/shared/analytics/posthog';
import { I18nProvider } from '@/shared/i18n';
import { redirectLegacyHostingOrigin } from '@/shared/lib/canonicalHost';
import { McapVirtualMcapProvider } from '@/shared/mcapVirtual/McapVirtualMcapProvider';
import { QueryClientProvider, QueryErrorResetBoundary } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';

function startApp(): void {
  registerSessionExpiredNavigator(async (destination) => {
    if (router.state.location.pathname === destination) return;
    await router.navigate({ to: destination, replace: true });
  });

  // Wire the explicitly selected authentication source into the auth store before render.
  // Idempotent, so StrictMode's double-invoke is harmless.
  initAuth();

  // Initialize product analytics before render so the provider picks up the
  // client. Idempotent and a no-op when POSTHOG_KEY is unset.
  initAnalytics();

  // Register app-wide dialogs (opened from global chrome, e.g. the sidebar) so
  // they are available on every route. Page-owned dialogs register on mount.
  registerAppDialogs();

  // Register app-wide keyboard shortcuts (e.g. ⌘/Ctrl-B to toggle the sidebar) so
  // they work on every route. Feature-owned shortcuts register on mount.
  registerAppShortcuts();

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element #root not found');
  }

  createRoot(rootElement).render(
    <StrictMode>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <AppSessionBoundary>
            <McapVirtualMcapProvider>
              <PostHogAppProvider>
                <QueryErrorResetBoundary>
                  <RouterProvider router={router} />
                </QueryErrorResetBoundary>
              </PostHogAppProvider>
            </McapVirtualMcapProvider>
          </AppSessionBoundary>
        </QueryClientProvider>
      </I18nProvider>
    </StrictMode>,
  );
}

if (!redirectLegacyHostingOrigin(window.location)) {
  startApp();
}
